import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import Logo from './Logo';
import { NAV_LINKS } from '../data/mock';

const DesktopNav = () => (
  <nav className="hidden lg:flex items-center gap-8">
    {NAV_LINKS.map((link) => (
      <NavLink
        key={link.path}
        to={link.path}
        className={({ isActive }) =>
          `text-sm tracking-wide uppercase font-medium transition-colors hover:text-[#1AB6FF] ${
            isActive ? 'text-[#1AB6FF]' : 'text-white/80'
          }`
        }
      >
        {link.label}
      </NavLink>
    ))}
  </nav>
);

const MobileNav = ({ onClose, onCTA }) => (
  <div className="lg:hidden bg-[#04070d]/95 backdrop-blur-lg border-t border-[#1AB6FF]/10">
    <div className="px-6 py-6 flex flex-col gap-4">
      {NAV_LINKS.map((link) => (
        <NavLink
          key={link.path}
          to={link.path}
          onClick={onClose}
          className={({ isActive }) =>
            `text-base font-medium ${isActive ? 'text-[#1AB6FF]' : 'text-white/85'}`
          }
        >
          {link.label}
        </NavLink>
      ))}
      <button
        onClick={onCTA}
        className="btn-cyan rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 mt-2"
      >
        Book Strategy Call <ArrowRight size={16} />
      </button>
    </div>
  </div>
);

const CTAButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="btn-cyan rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
  >
    Book Strategy Call
    <ArrowRight size={16} />
  </button>
);

const MobileToggle = ({ open, onToggle }) => (
  <button
    className="lg:hidden text-white"
    onClick={onToggle}
    aria-label="Toggle menu"
  >
    {open ? <X size={26} /> : <Menu size={26} />}
  </button>
);

const useHeaderScroll = () => {
  const [scrolled, setScrolled] = useState(false);
  const onScroll = useCallback(() => setScrolled(window.scrollY > 20), [setScrolled]);
  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);
  return scrolled;
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const scrolled = useHeaderScroll();
  const navigate = useNavigate();

  const goContact = useCallback(() => {
    setOpen(false);
    navigate('/contact');
  }, [navigate, setOpen]);

  const toggleMenu = useCallback(() => setOpen((v) => !v), [setOpen]);
  const closeMenu = useCallback(() => setOpen(false), [setOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#04070d]/85 backdrop-blur-md border-b border-[#1AB6FF]/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="shrink-0">
          <Logo />
        </Link>
        <DesktopNav />
        <div className="hidden lg:block">
          <CTAButton onClick={goContact} />
        </div>
        <MobileToggle open={open} onToggle={toggleMenu} />
      </div>

      {open && <MobileNav onClose={closeMenu} onCTA={goContact} />}
    </header>
  );
};

export default Header;
